/**
 * "Teste o Premium por 30 dias sem pagar nada a mais" (PIT-535): the offer the upgrade journey
 * (n8n, PIT-541) sends to a paying monthly Basic customer.
 *
 * The customer authorizes the Premium charge first (the card on file, or a new variable Pix
 * Automático with the Premium ceiling), gets Premium at once, and pays nothing until the first
 * Premium charge, 30 days after accepting. Giving up before that goes back to Basic on the same
 * agreement, with no new authorization. Once per account.
 *
 * Not a lead offer: it is not in CHECKOUT_OFFER_KINDS, so the public checkout never resolves it.
 */
import {
  clampDeferredFirstChargeAt,
  openPixStartsOn,
  saoPauloDay,
  startOfSaoPauloDay,
  utcDay,
} from "./provider_first_charge.js";
import { getPlanPricing } from "./plans.js";
import type { Currency, PaymentMethod } from "./types.js";

export { saoPauloDay, startOfSaoPauloDay };

export const PREMIUM_TRIAL_OFFER_KIND = "premium_trial_30d" as const;
export type PremiumTrialOfferKind = typeof PREMIUM_TRIAL_OFFER_KIND;

export const PREMIUM_TRIAL_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Why the offer is not created for a customer (422 `not_eligible` with `reason` and `final`). */
export const PREMIUM_TRIAL_INELIGIBLE_REASONS = [
  "not_found",
  "not_basic",
  "not_monthly",
  "not_active",
  "cancel_scheduled",
  "refunded",
  "already_used",
  "locale_not_supported",
  /**
   * A pt-BR customer whose card is not billed in BRL. The card's currency follows the language the
   * billing page last saw, which a visit with the app in another language changes back and forth.
   */
  "currency_not_brl",
  /** The id is shared, or the email n8n sent is not this subscription's customer: never guessed. */
  "ambiguous",
] as const;
export type PremiumTrialIneligibleReason = (typeof PREMIUM_TRIAL_INELIGIBLE_REASONS)[number];

/**
 * The reasons that do not change on their own: n8n takes the customer out of the journey on these
 * only (`final: true`). The others can pass later and n8n asks again: `not_found` until the customer
 * has a zenpay row (the first open of the billing page makes it), `not_active` until a renewal is
 * paid or another change finishes, `cancel_scheduled` until a stopped renewal is undone,
 * `currency_not_brl` until the billing page sees the customer in Portuguese again (Sadraque, 28/10).
 */
const PREMIUM_TRIAL_FINAL_REASONS: ReadonlySet<PremiumTrialIneligibleReason> = new Set<PremiumTrialIneligibleReason>([
  "already_used",
  "not_basic",
  "not_monthly",
  "refunded",
  "locale_not_supported",
  "ambiguous",
]);

export function premiumTrialReasonIsFinal(reason: PremiumTrialIneligibleReason): boolean {
  return PREMIUM_TRIAL_FINAL_REASONS.has(reason);
}

export interface PremiumTrialSubscriptionSnapshot {
  plan: string;
  interval: string;
  status: string;
  paymentMethod: string;
  currentPeriodEnd: Date | null;
}

/**
 * Who can get the offer, decided in this order so n8n gets the most useful reason. Only pt-BR
 * monthly Basic paying by card (Stripe) or Pix Automático: the annual and Stone 12x customers stay
 * out in this version (owner, 19/10).
 */
export function premiumTrialEligibility(args: {
  locale: string;
  /** What the subscription is billed in: the offer is priced in BRL only. */
  currency: Currency;
  subscription: PremiumTrialSubscriptionSnapshot | null;
  openCancellation: boolean;
  /** Any other open scheduled change (a downgrade, a payment method change, an unapproved setup). */
  otherOpenScheduledChange: boolean;
  /** The latest paid order was refunded. */
  refunded: boolean;
  /** This customer already redeemed the offer. */
  alreadyUsed: boolean;
  now: Date;
}): { ok: true } | { ok: false; reason: PremiumTrialIneligibleReason } {
  if (args.locale !== "pt-BR") return { ok: false, reason: "locale_not_supported" };
  const sub = args.subscription;
  if (!sub) return { ok: false, reason: "not_found" };
  if (args.alreadyUsed) return { ok: false, reason: "already_used" };
  if (args.refunded) return { ok: false, reason: "refunded" };
  if (args.openCancellation) return { ok: false, reason: "cancel_scheduled" };
  if (sub.plan !== "basic") return { ok: false, reason: "not_basic" };
  if (sub.interval !== "monthly") return { ok: false, reason: "not_monthly" };
  // After the final reasons: a USD customer who already used the trial leaves the journey instead
  // of being asked again for a currency that may never turn BRL.
  if (args.currency !== "BRL") return { ok: false, reason: "currency_not_brl" };
  if (sub.paymentMethod !== "stripe" && sub.paymentMethod !== "openpix") return { ok: false, reason: "not_active" };
  if (sub.status !== "active" || args.otherOpenScheduledChange) return { ok: false, reason: "not_active" };
  if (!sub.currentPeriodEnd || sub.currentPeriodEnd.getTime() <= args.now.getTime()) {
    return { ok: false, reason: "not_active" };
  }
  return { ok: true };
}

/**
 * The first Premium charge: 30 days after accepting, moved to a date the provider really charges
 * on. On PIX it is a day of the month (Woovi's `dayGenerateCharge`, 1–27) that Woovi places from
 * today and refuses under 3 days away (`openPixStartsOn`), so only dates from today + 3 up to the
 * day before today's day next month can be reached: day 30 when Woovi starts on it, else the last
 * date before it that it does. Until 30/10/2026 this assumed Woovi counted from today + 3 and skipped
 * a month, so a day 30 past the 27th went to the 1st two months on, and Woovi refused it ("pelo menos
 * três dias a partir de hoje") whenever the coming 1st was under 3 days away. So on PIX the trial runs
 * 26–30 days counted on Brazil's calendar (the screens show the date, not "30 days"). Noon UTC, as
 * "Manter assinatura" does: shown in Brazil, midnight UTC is the evening before; the day of the
 * month is the same.
 *
 * Which "today" Woovi counts from (the UTC date or the São Paulo date) is not confirmed. They differ
 * from 21:00 to 24:00 BRT, so only a day that lands on the same date under both is used (PIT-535
 * review, 27/10); the e2e checks an evening accept against Woovi's `GET installments`.
 */
export function premiumTrialFirstChargeAt(paymentMethod: PaymentMethod, now: Date = new Date()): Date {
  const target = new Date(now.getTime() + PREMIUM_TRIAL_DAYS * DAY_MS);
  if (paymentMethod !== "openpix") return clampDeferredFirstChargeAt({ paymentMethod, firstChargeAt: target, now });
  return new Date(openPixReachableChargeDay(target, now).getTime() + DAY_MS / 2);
}

function openPixReachableChargeDay(target: Date, now: Date): Date {
  const reachable = (day: Date) => openPixStartsOn(day, now);
  const start = utcDay(target);
  for (let shift = 0; shift <= 7; shift++) {
    const day = new Date(start.getTime() + shift * DAY_MS);
    if (reachable(day)) return day;
  }
  for (let shift = 1; shift <= 7; shift++) {
    const day = new Date(start.getTime() - shift * DAY_MS);
    if (reachable(day)) return day;
  }
  throw new Error("premium_trial_no_pix_charge_day");
}

/**
 * What the agreement charges: Premium or Basic list plus the paid add-ons the subscription keeps
 * (monthly). On PIX the customer authorizes the Premium amount as the ceiling, so going back to Basic
 * lowers the value under it with no new authorization.
 */
export function premiumTrialAmounts(currency: Currency, addOnMonthlyCents: number) {
  const premiumCents = getPlanPricing("premium", "monthly", currency).totalCents + addOnMonthlyCents;
  const basicCents = getPlanPricing("basic", "monthly", currency).totalCents + addOnMonthlyCents;
  return { premiumCents, basicCents, authorizedCeilingCents: premiumCents };
}

/**
 * The link n8n sends (app card, WhatsApp). It carries only the offer token: the page trades it for
 * a short-lived billing session while the offer is valid (`POST /api/integrated/offer-session`).
 */
export function buildPremiumTrialOfferUrl(args: { publicAppUrl: string; token: string; locale: string }): string {
  const params = new URLSearchParams({ offer: args.token, app_language: args.locale });
  return `${args.publicAppUrl.replace(/\/$/, "")}/integrated?${params.toString()}`;
}

/** What the billing page shows about the offer. */
export type PremiumTrialPageState =
  | { status: "available"; offer_token: string; first_charge_at: string; premium_cents: number; basic_cents: number }
  /** "Testar 30 dias" on PIX, waiting for the bank app: the page reopens that QR, never a new one. */
  | { status: "pending_authorization"; order_id: string; first_charge_at: string; premium_cents: number }
  /**
   * `give_up_until`: when "Voltar ao Básico" closes (premiumTrialGiveUpClosesAt: on PIX the start of
   * the day before the charge day in Brazil, on the card the start of the charge day); `can_give_up`
   * turns false then.
   */
  | {
      status: "active";
      first_charge_at: string;
      premium_cents: number;
      basic_cents: number;
      can_give_up: boolean;
      give_up_until: string;
    }
  | { status: "given_up"; first_charge_at: string; basic_cents: number };

/**
 * The trial is running (and can be given up) from the approval until its first Premium charge, as
 * long as the subscription is still the active Premium monthly that came from it. A stopped renewal
 * (status canceled) or a refund (expired) ends it: going back to Basic would bring that back.
 */
export function premiumTrialIsRunning(args: {
  offer: { redeemedAt: Date | null; trialEndsAt: Date | null; givenUpAt: Date | null };
  subscription: { plan: string; interval: string; status: string; offerId: string | null };
  offerId: string;
  now: Date;
}): boolean {
  const { offer, subscription } = args;
  if (!offer.redeemedAt || !offer.trialEndsAt || offer.givenUpAt) return false;
  if (offer.trialEndsAt.getTime() <= args.now.getTime()) return false;
  return (
    subscription.plan === "premium" &&
    subscription.interval === "monthly" &&
    subscription.status === "active" &&
    subscription.offerId === args.offerId
  );
}

/**
 * When "Voltar ao Básico" closes. On PIX, when the day before the trial's charge day starts in
 * Brazil: Woovi generates the installment (at the Premium amount) on the charge day or earlier, at a
 * time nobody has checked, and zenpay would then book Basic while the bank collects Premium (PIT-535
 * review, 27/10; owner, 28/10: a day of margin). The card when the charge day starts in Brazil:
 * Stripe charges at `trial_end` itself, and the hours left keep a give-up clear of it.
 */
export function premiumTrialGiveUpClosesAt(trialEndsAt: Date, paymentMethod: string): Date {
  const chargeDayStart = startOfSaoPauloDay(trialEndsAt);
  if (paymentMethod !== "openpix") return chargeDayStart;
  // Noon of the day before is inside that day whatever the offset, so its start is the BRT midnight.
  return startOfSaoPauloDay(new Date(chargeDayStart.getTime() - DAY_MS / 2));
}

/**
 * The period up to the trial's first charge is unpaid: from the approval to that date, whatever
 * the subscription did since (the trial running, given up, Premium again in one click, a stopped
 * renewal, or a normal Premium change that cleared the subscription's offer). Keyed on the
 * customer's redeemed offer, not the subscription's `offer_id`, which a later change rewrites. A
 * switch to annual in that window would credit those days against the annual price. An expired
 * subscription is out: reactivating charges in full, with no credit.
 */
export function premiumTrialPeriodUnpaid(args: {
  offer: { redeemedAt: Date | null; trialEndsAt: Date | null };
  subscription: { status: string };
  now: Date;
}): boolean {
  const { offer } = args;
  if (!offer.redeemedAt || !offer.trialEndsAt) return false;
  if (args.subscription.status === "expired") return false;
  return offer.trialEndsAt.getTime() > args.now.getTime();
}
